import { Brain, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { type JSX, useEffect, useRef, useState } from "react";

import { ChatMarkdown } from "@/components/chat/chat-markdown";
import { cn } from "@/lib/utils";

interface ThinkingBlockProps {
  text: string;
  /** True while reasoning is still streaming; flips false on done/completed. */
  active: boolean;
}

/**
 * Collapsible "Thinking" panel rendered above an assistant bubble's
 * user-visible text. Auto-expands while reasoning is streaming so the user
 * sees the model "working", then auto-collapses once the visible answer
 * starts (or the response finishes) to keep the transcript focused.
 *
 * Visually distinct from the normal bubble:
 *  - muted left border + lemon brain icon
 *  - smaller / italicised body
 *  - chevron toggle
 */
export function ThinkingBlock({ text, active }: ThinkingBlockProps): JSX.Element | null {
  const [open, setOpen] = useState<boolean>(active);
  // Track whether the user has manually toggled, so auto-collapse on
  // completion does not yank a panel the user explicitly opened.
  const userTouchedRef = useRef<boolean>(false);

  // Auto-expand the moment new reasoning streams in, auto-collapse when
  // streaming ends — but only while the user hasn't taken control.
  useEffect(() => {
    if (userTouchedRef.current) return;
    setOpen(active);
  }, [active]);

  if (!text && !active) return null;

  const onToggle = (): void => {
    userTouchedRef.current = true;
    setOpen((v) => !v);
  };

  return (
    <div
      className={cn(
        "my-1 rounded-md border border-border/60 bg-muted/40 text-muted-foreground",
        active && "border-[hsl(var(--lemon))]/40 bg-[hsl(var(--lemon))]/5",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center gap-1.5 px-2 py-1 text-[11px] font-medium",
          "hover:bg-muted/60",
        )}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Brain className="h-3 w-3 text-[hsl(var(--lemon))]" />
        <span>Thinking</span>
        {active ? (
          <Loader2 className="ml-1 h-3 w-3 animate-spin text-[hsl(var(--lemon))]" />
        ) : text ? (
          <span className="ml-1 font-mono text-[10px] opacity-70">
            {text.length.toLocaleString()} chars
          </span>
        ) : null}
      </button>
      {open && text && (
        <div className="border-t border-border/60 px-3 py-2 text-[12px] italic leading-relaxed">
          <ChatMarkdown source={text} />
        </div>
      )}
    </div>
  );
}
